import { GridActionsCellItem } from "@mui/x-data-grid";
// Hooks
import { useTranslation } from "react-i18next";
import { tokens } from "src/locales/tokens";
import { useRouter } from "src/hooks/use-router";
// Icons
import TableRowsIcon from "@mui/icons-material/TableRows";
import { CustomTooltip } from "src/components/inputs";
import DeleteIcon from "src/icons/tmw-ui/tmw-delete-icon.png";
import { Box, Chip } from "@mui/material";
import ViewCompactIcon from "@mui/icons-material/ViewCompact";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import { Check, Close } from "@mui/icons-material";

// Status Cell custom Component
const statusCellComponent = (status) => {
  return status ? (
    <Check style={{ color: "#39B349" }} />
  ) : (
    <Close style={{ color: "#FF0000" }} />
  );
};

export const dataGridStyle = {
  fontFamily: "tmwCustomFont",
  "& .MuiDataGrid-columnHeaders": {
    borderTop: "1px solid #E6E6E6",
    borderRadius: 0,
    borderBottom: "1px solid #E6E6E6",
  },
  "& .MuiInputBase-input": {
    fontSize: 12,
    fontWeight: 500,
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: 600,
  },
  "& .MuiDataGrid-columnHeader--emptyGroup": {
    outline: "none !important",
  },
  "& .MuiDataGrid-columnHeader:not(:first-of-type)": {
    borderLeft: "1px solid #E6E6E6",
    outline: "none",
  },
  "& .MuiDataGrid-cell": {
    borderLeft: "1px solid #E6E6E6",
    letterSpacing: 1,
    fontSize: 12,
    fontWeight: 400,
  },
  "& .MuiDataGrid-cell:last-child": {
    borderRight: "1px solid #E6E6E6",
  },
  "& .MuiDataGrid-row:not(:last-child)": {
    paddingTop: "1px",
    borderBottom: "1px solid #E6E6E6",
  },
  "& .MuiDataGrid-columnSeparator": {
    opacity: "0 !important",
  },
  "& .first-header-style": {
    borderLeft: "1px solid #E6E6E6 !important",
    outline: "none !important",
  },
  "& .last-header-style": {
    borderRight: "1px solid #E6E6E6 !important",
  },
  "& .header-style": {
    letterSpacing: 2,
    fontSize: 10,
    fontWeight: 600,
    textTransform: "uppercase",
  },
  "& .actions": {
    borderLeft: "1px solid #E6E6E6",
    display: "flex",
    justifyContent: "space-around",
  },
  "& .MuiDataGrid-footerContainer": {
    letterSpacing: 2,
    textTransform: "uppercase",
    fontSize: 11,
  },
  "& .MuiTablePagination-selectLabel": {
    fontSize: 11,
    letterSpacing: 1,
    fontFamily: "tmwCustomFont",
  },
  "& .MuiTablePagination-displayedRows": {
    fontSize: 11,
    letterSpacing: 1,
    fontFamily: "tmwCustomFont",
  },
  "& .MuiTablePagination-select": {
    fontSize: 11,
    letterSpacing: 1,
    fontFamily: "tmwCustomFont",
  },
  "& .MuiDataGrid-virtualScroller": {
    borderBottom: "1px solid #E6E6E6",
    letterSpacing: 2,
    fontSize: 10,
    fontWeight: 600,
    minHeight: "50px",
  },
  "& .column-groups": {
    marginTop: "0.5px",
  },
  "& .archived": {
    backgroundColor: "#FFEDED",
    "&:hover": {
      backgroundColor: "#F8EBEB",
    },
  },
};

export const columnsDefinition = (groupTypes, userGroup, suppliers) => {
  const { t } = useTranslation();
  const router = useRouter();

  const columns = [
    {
      field: "actions",
      type: "actions",
      headerName: "Row actions",
      renderHeader: () => (
        <TableRowsIcon
          style={{
            transform: "rotate(90deg) scale(.95, 1)",
          }}
        />
      ),
      headerClassName: "first-header-style",
      flex: 0.4,
      getActions: ({ id, row }) => {
        return [
          <CustomTooltip
            title="View Details"
            key={"view-details-tooltip-" + id}
          >
            <GridActionsCellItem
              icon={
                <ViewCompactIcon
                  style={{ fontSize: 23, marginRight: "-5px" }}
                />
              }
              label="View Details"
              className="textPrimary"
              key={"viewDetails-" + id}
              onClick={() =>
                router.push("/dashboard/suppliers/" + id + "/details")
              }
            />
          </CustomTooltip>,
        ];
      },
    },
    {
      field: "supplierId",
      headerName: "ID",
      headerClassName: "header-style",
      flex: 0.7,
      editable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "supplierCode",
      headerName: "Code",
      headerClassName: "header-style",
      flex: 1,
      editable: false,
      headerAlign: "center",
    },
    {
      field: "companyName",
      headerName: "Company",
      headerClassName: "header-style",
      flex: 1,
      editable: false,
      headerAlign: "center",
    },
    {
      field: "email",
      headerName: "Email",
      headerClassName: "header-style",
      flex: 0.8,
      editable: false,
      headerAlign: "center",
    },
    {
      field: "portalAccess",
      headerName: "Portal Access",
      headerClassName: "header-style",
      flex: 0.6,
      editable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (cell) => {
        return (
          <CustomTooltip
            title={`${cell.value ? "Yes" : "No"}`}
            key={"supplier-has-portal-access-tooltip-" + cell.row.id}
          >
            {statusCellComponent(cell.value)}
          </CustomTooltip>
        );
      },
    },
    {
      field: "createdAt",
      headerName: "Created",
      headerClassName: "header-style",
      flex: 0.5,
      editable: false,
      headerAlign: "center",
      renderCell: (row) => {
        return moment(row.createdAt).format("DD/MM/YYYY HH:mm");
      },
    },
    {
      field: "updatedAt",
      headerName: "Updated",
      headerClassName: "header-style last-header-style",
      flex: 0.5,
      editable: false,
      headerAlign: "center",
      renderCell: (row) => {
        return moment(row.updatedAt).format("DD/MM/YYYY HH:mm");
      },
    },
  ];

  return columns;
};
